import type { IControl } from "maplibre-gl";
import { html, LitElement, svg } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { layersIcon } from "./icons.ts";
import { REFERENCE_LAYERS, type ReferenceLayer } from "./reference-layers.ts";
import { HAULOUT_COLOR } from "./style.ts";
import hydrophoneIcon from './assets/hydrophone-default.svg?url';
import salmonCountingSiteIcon from './assets/salmon-counting-site.svg?url';
import viewingLocationIcon from './assets/viewing-location.svg?url';

type Options = {
  visible: ReadonlySet<ReferenceLayer>;
  onChange: (visible: Set<ReferenceLayer>) => void;
};

/**
 * The switch for the map's reference layers (GH #453, decision 058): a button
 * in the map's top-right corner that opens a checkbox per layer. Each row shows
 * the layer's own marker, so the menu is also the legend.
 *
 * Like the location control, a MapLibre control whose element is grafted into
 * the map, so it sits with the zoom buttons and takes their styling. It holds no
 * state of its own beyond whether it is open: `<obs-map>` sets which layers are
 * visible, and a change goes back up through `onChange`.
 */
export default class LayerControl implements IControl {
  readonly #element: LayerControlElement;
  readonly #container = document.createElement('div');

  constructor({visible, onChange}: Options) {
    const element = document.createElement('layer-control');
    element.visible = visible;
    element.onChange = onChange;
    this.#element = element;
    this.#container.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    this.#container.append(element);
  }

  onAdd() {
    return this.#container;
  }

  onRemove() {
    this.#container.remove();
  }

  set visible(visible: ReadonlySet<ReferenceLayer>) {
    this.#element.visible = visible;
  }

  /** The layers the map's style has, which are the ones the menu offers. */
  set available(available: ReadonlySet<ReferenceLayer>) {
    this.#element.available = available;
  }
}

const markers: Record<ReferenceLayer, unknown> = {
  viewpoints: html`<img src=${viewingLocationIcon} alt="">`,
  hydrophones: html`<img src=${hydrophoneIcon} alt="">`,
  salmon: html`<img src=${salmonCountingSiteIcon} alt="">`,
  haulouts: html`<svg viewBox="0 0 16 16" aria-hidden="true">${svg`<path d="M8 2 14 8 8 14 2 8Z" fill="white" stroke=${HAULOUT_COLOR} stroke-width="2"/>`}</svg>`,
  // Two regions side by side, in hues as the basemap draws them: data colours, each region's own.
  regions: html`<svg viewBox="0 0 16 16" aria-hidden="true">${svg`<path d="M2 3h6v10H2Z" fill="hsl(200 65% 55% / 0.45)" stroke="#1f3a5f" stroke-width="0.7"/><path d="M8 3h6v10H8Z" fill="hsl(337 65% 55% / 0.45)" stroke="#1f3a5f" stroke-width="0.7"/>`}</svg>`,
};

@customElement('layer-control')
class LayerControlElement extends LitElement {
  @property({attribute: false})
  visible: ReadonlySet<ReferenceLayer> = new Set();

  /** The layers to offer: every one, until the map says which its style has. */
  @property({attribute: false})
  available: ReadonlySet<ReferenceLayer> = new Set(REFERENCE_LAYERS.map(l => l.id));

  onChange: (visible: Set<ReferenceLayer>) => void = () => {};

  @state()
  private open = false;

  protected override createRenderRoot() { return this; }

  // Closes on a press anywhere else, or Escape. composedPath() sees through
  // the map's shadow root, which the event's target does not.
  //
  // A press on the map while the menu is open only closes it. Tapping the map
  // is how a phone dismisses a menu, and with haul-outs on, that tap would
  // otherwise land on a site and leave the page. Listening in the capture
  // phase is what lets it stop the press before the map sees it.
  #onDocumentPointerDown = (e: PointerEvent) => {
    if (!this.open)
      return;
    const path = e.composedPath();
    if (path.includes(this))
      return;
    this.open = false;
    const viewport = this.closest('.maplibregl-map');
    if (viewport && path.includes(viewport)) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  #onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.open) {
      this.open = false;
      this.querySelector('button')?.focus();
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, {capture: true});
    this.addEventListener('keydown', this.#onKeyDown);
  }

  override disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, {capture: true});
    this.removeEventListener('keydown', this.#onKeyDown);
    super.disconnectedCallback();
  }

  #toggle(layer: ReferenceLayer, on: boolean) {
    const visible = new Set(this.visible);
    if (on) visible.add(layer); else visible.delete(layer);
    this.onChange(visible);
  }

  protected render() {
    return html`
      <button type="button" title="Map layers" aria-label="Map layers"
          aria-expanded=${this.open ? 'true' : 'false'} aria-controls="layer-menu"
          @click=${() => this.open = !this.open}>
        <svg class="inline-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">${layersIcon}</svg>
      </button>
      ${this.open ? html`
        <fieldset id="layer-menu" class="layer-menu">
          <legend>Map layers</legend>
          ${REFERENCE_LAYERS.filter(({id}) => this.available.has(id)).map(({id, label}) => html`
            <label>
              <input type="checkbox" .checked=${this.visible.has(id)}
                  @change=${(e: Event) => this.#toggle(id, (e.target as HTMLInputElement).checked)}>
              <span class="marker">${markers[id]}</span>
              ${label}
            </label>
          `)}
        </fieldset>
      ` : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "layer-control": LayerControlElement;
  }
}
